"use client";
import { useEffect, useMemo, useState } from "react";
import { buildOpeningsServicePayload } from "@openings/booking-core";
import type { LocationOpeningsItem, DbDateString } from "@openings/api-client";
import type { BookingOpeningsClient } from "../types";

/**
 * Fetches member openings for a schedule via the abstract BookingOpeningsClient.
 * Port of apps/web useBookingOpenings hook — decoupled from the web proxy client.
 */
export function useScheduleMemberOpenings({
  openingsClient,
  salonId,
  scheduleId,
  date,
  selectedServices,
  refreshCount = 0,
}: {
  openingsClient: BookingOpeningsClient;
  salonId: string;
  scheduleId: string;
  date: DbDateString;
  selectedServices: Array<{ id: string; option?: { id: string } | null }>;
  refreshCount?: number;
}) {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<LocationOpeningsItem[]>([]);
  const [resolvedQueryKey, setResolvedQueryKey] = useState<string | null>(
    null,
  );

  const servicesPayload = useMemo(
    () => buildOpeningsServicePayload(selectedServices),
    [selectedServices],
  );
  const servicesKey = JSON.stringify(servicesPayload);
  const queryKey = `${salonId}:${scheduleId}:${date}:${servicesKey}:${refreshCount}`;

  useEffect(() => {
    if (
      !servicesPayload.length ||
      !scheduleId ||
      !openingsClient.getScheduleMemberOpenings
    ) {
      setMembers([]);
      setResolvedQueryKey(queryKey);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchOpenings = async () => {
      setLoading(true);
      setMembers([]);
      setResolvedQueryKey(null);
      try {
        const res = await openingsClient.getScheduleMemberOpenings!({
          salonId,
          scheduleId,
          date,
          services: servicesPayload,
          // Keep members who are on vacation in the list so the panel can say
          // they are away until a date. Without this the API omits them and a
          // regular customer just sees the stylist vanish for a fortnight with
          // no explanation.
          includeVacation: true,
        });
        if (!cancelled) {
          setMembers(res.data);
          setResolvedQueryKey(queryKey);
        }
      } catch (error) {
        console.error("Failed to fetch openings:", error);
        if (!cancelled) {
          setMembers([]);
          setResolvedQueryKey(queryKey);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOpenings();

    return () => {
      cancelled = true;
    };
  }, [
    salonId,
    scheduleId,
    date,
    servicesKey,
    refreshCount,
    openingsClient,
    queryKey,
  ]);

  return {
    loading: loading || resolvedQueryKey !== queryKey,
    members: resolvedQueryKey === queryKey ? members : [],
  };
}
